import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  Alert,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  SelectChangeEvent,
  CircularProgress,
  Autocomplete,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IrishCounty } from '../../types/BandRecommendations';
import { instruments } from '../../constants/instrumentation';
import jazzSubgenres, { popularJazzSubgenres, jazzSubgenresGrouped } from '../../data/jazzSubgenres';

interface Performer {
  name: string;
}

interface SelectedInstrument {
  id: string;
  name: string;
  withEffects: boolean;
  performer?: Performer;
}

const ArtistProfileEditPage = () => {
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    county: '',
    postalCode: '',
    phonePrefix: '',
    phoneNumber: '',
    bio: '',
    genres: [] as string[],
    website: '',
    facebookPage: '',
    twitterHandle: '',
    instagramHandle: '',
    tikTokHandle: '',
    instruments: [] as SelectedInstrument[],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openInstrumentDialog, setOpenInstrumentDialog] = useState(false);
  const [currentInstrument, setCurrentInstrument] = useState<SelectedInstrument | null>(null);
  const [profileImage, setProfileImage] = useState<string>('');
  const [profileImagePosition, setProfileImagePosition] = useState<string>('center center');
  const [photoUploading, setPhotoUploading] = useState(false);

  // Safely flatten instruments array
  const flattenedInstruments = React.useMemo(() => {
    if (!Array.isArray(instruments)) return [];
    return instruments.reduce((acc, category) => {
      if (category && Array.isArray(category.options)) {
        return [...acc, ...category.options];
      }
      return acc;
    }, [] as { id: string; name: string }[]);
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile data');
      }

      const data = await response.json();
      setProfileData({
        ...data,
        genres: data.genres || [],
        instruments: data.instruments || [],
      });
      setProfileImage(data.profileImage || '');
      setProfileImagePosition(data.profileImagePosition || 'center center');
    } catch (err) {
      toast.error('Failed to load profile data. Please try again.');
      console.error('Error fetching profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenreChange = (event: React.SyntheticEvent, value: string[]) => {
    setProfileData(prev => ({ ...prev, genres: value.slice(0, 4) }));
  };

  const handleInstrumentSelect = (event: React.SyntheticEvent, value: { id: string; name: string } | null) => {
    if (value && profileData.instruments.length < 3) {
      setCurrentInstrument({
        id: value.id,
        name: value.name,
        withEffects: false,
      });
      setOpenInstrumentDialog(true);
    }
  };

  const handleAddInstrument = () => {
    if (currentInstrument) {
      setProfileData(prev => ({
        ...prev,
        instruments: [...prev.instruments, currentInstrument]
      }));
      setOpenInstrumentDialog(false);
      setCurrentInstrument(null);
    }
  };

  const handleRemoveInstrument = (id: string) => {
    setProfileData(prev => ({
      ...prev,
      instruments: prev.instruments.filter(instrument => instrument.id !== id)
    }));
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setPhotoUploading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/profile-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      setProfileImage(data.imageUrl);
      toast.success('Photo uploaded.');
    } catch (err) {
      toast.error('Failed to upload photo. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...profileData, profileImage, profileImagePosition }),
      });

      if (!response.ok) throw new Error('Failed to update profile');

      toast.success('Profile saved.');
    } catch (err) {
      toast.error('Failed to save profile. Please try again.');
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom>
        Edit Your Artist Profile
      </Typography>
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{ mt: 2, mb: 1 }}
      >
        Most information on this profile is <strong>private</strong> and visible only to you. Where something is public it is noted below. You can update or delete your profile at any time — though note that deleting your profile will remove you from search results and unlink your bands.
      </Alert>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <Grid container spacing={2}>

            {/* Profile Photo + Focal Point Picker */}
            <Grid item xs={12}>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: profileImage ? 1.5 : 0 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={photoUploading}
                    size="small"
                  >
                    {photoUploading ? 'Uploading...' : profileImage ? 'Change Photo' : 'Upload Profile Photo'}
                    <input type="file" hidden accept="image/jpeg,image/png,image/gif" onChange={handlePhotoUpload} />
                  </Button>
                  {profileImage && (
                    <Button size="small" color="error" onClick={() => setProfileImage('')}>
                      Remove
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    JPEG or PNG, max 10MB. <strong>Public</strong> — shown on your artist profile page.
                  </Typography>
                </Box>

                {profileImage && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Click the preview below to set where the image is anchored in your public page banner.
                    </Typography>
                    {/* Focal point picker — 16:9 preview matching the hero */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 560,
                        aspectRatio: '16/5',
                        overflow: 'hidden',
                        borderRadius: 1,
                        cursor: 'crosshair',
                        border: '2px solid',
                        borderColor: 'primary.main',
                      }}
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                        const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
                        setProfileImagePosition(`${x}% ${y}%`);
                      }}
                    >
                      <Box
                        component="img"
                        src={profileImage}
                        alt="Focal point preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: profileImagePosition,
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }}
                      />
                      {/* Crosshair marker */}
                      {(() => {
                        const parts = profileImagePosition.split(' ');
                        const px = parseFloat(parts[0]) || 50;
                        const py = parseFloat(parts[1]) || 50;
                        return (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: `${px}%`,
                              top: `${py}%`,
                              transform: 'translate(-50%, -50%)',
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              border: '2px solid white',
                              boxShadow: '0 0 0 2px rgba(0,0,0,0.6)',
                              pointerEvents: 'none',
                            }}
                          />
                        );
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label="Full Name"
                value={profileData.name}
                onChange={handleChange}
                helperText="Public — shown on your artist profile page and in search results."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="email"
                label="Email Address"
                type="email"
                value={profileData.email}
                InputProps={{
                  readOnly: true,
                }}
                variant="filled"
                helperText="Private — used to identify your account. The only time a programmer sees your email is if you choose to add it as contact information on one of your band pages."
              />
            </Grid>

            <Grid item xs={12} sx={{ pb: 0 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Address — Private
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Used for any applications you submit to IMC (e.g. funding or grant applications) and for future services such as a musicians directory. Never shown publicly.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="streetAddress1"
                label="Street Address"
                value={profileData.streetAddress1}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="streetAddress2"
                label="Street Address Line 2"
                value={profileData.streetAddress2}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="city"
                label="City"
                value={profileData.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>County</InputLabel>
                <Select
                  name="county"
                  value={profileData.county ?? ''}
                  onChange={handleSelectChange}
                  label="County"
                >
                  {Object.values(IrishCounty).map((county) => (
                    <MenuItem key={county} value={county}>{county}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="postalCode"
                label="Postal Code / Eircode"
                value={profileData.postalCode}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                name="phonePrefix"
                label="Phone Prefix"
                value={profileData.phonePrefix}
                onChange={handleChange}
                inputProps={{ maxLength: 3 }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                name="phoneNumber"
                label="Phone Number"
                value={profileData.phoneNumber}
                onChange={handleChange}
                helperText="Private — so IMC can contact you directly if needed. Never shared with programmers."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="bio"
                label="Bio"
                value={profileData.bio}
                onChange={handleChange}
                helperText="Public — shown on your artist profile page."
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="genres"
                options={jazzSubgenresGrouped}
                groupBy={(option) => popularJazzSubgenres.includes(option) ? 'Popular' : 'All Genres'}
                value={profileData.genres}
                onChange={handleGenreChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Jazz Sub-genres (Select up to 4)"
                    fullWidth
                    helperText="Used to group artists together and help programmers discover music similar to what they already like. These can be changed at any time."
                  />
                )}
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                  ))
                }
                limitTags={4}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={flattenedInstruments}
                getOptionLabel={(option) => option.name}
                onChange={handleInstrumentSelect}
                disabled={profileData.instruments.length >= 3}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add Instrument (max 3)"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {profileData.instruments.map((instrument) => (
                            <Chip
                              key={instrument.id}
                              variant="outlined"
                              label={`${instrument.name}${instrument.withEffects ? ' (with effects)' : ''}${instrument.performer ? ` - ${instrument.performer.name}` : ''}`}
                              onDelete={() => handleRemoveInstrument(instrument.id)}
                              size="small"
                              sx={{ m: 0.5 }}
                            />
                          ))}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sx={{ pb: 0 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Links — Public
              </Typography>
              <Typography variant="caption" color="text.secondary">
                These appear on your public artist profile page so visitors can find you across the web.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Tooltip title="This is your primary website where all your information can be found, including links to your various bands. When you create a band page, you can have a separate main website for the band, but this is for you as an individual artist.">
                <TextField
                  fullWidth
                  name="website"
                  label="Main Website"
                  value={profileData.website}
                  onChange={handleChange}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="facebookPage"
                label="Facebook Page"
                value={profileData.facebookPage}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="twitterHandle"
                label="Twitter Handle"
                value={profileData.twitterHandle}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="instagramHandle"
                label="Instagram Handle"
                value={profileData.instagramHandle}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="tikTokHandle"
                label="TikTok Handle"
                value={profileData.tikTokHandle}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

        <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : 'Save Profile'}
        </Button>
      </Box>

      <Dialog open={openInstrumentDialog} onClose={() => setOpenInstrumentDialog(false)}>
        <DialogTitle>Instrument Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentInstrument?.withEffects || false}
                  onChange={(e) => setCurrentInstrument(curr => curr ? {
                    ...curr,
                    withEffects: e.target.checked
                  } : null)}
                />
              }
              label="Uses effects"
            />
            <TextField
              fullWidth
              label="Performer Name"
              sx={{ mt: 2 }}
              value={currentInstrument?.performer?.name || ''}
              onChange={(e) => setCurrentInstrument(curr => curr ? {
                ...curr,
                performer: { name: e.target.value }
              } : null)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInstrumentDialog(false)}>Cancel</Button>
          <Button onClick={handleAddInstrument} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ArtistProfileEditPage;